import React from "react";
import { Alert, Box, Button, Fade, LinearProgress, Paper, Tooltip, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BoltIcon from "@mui/icons-material/Bolt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { formatBytes, formatEta, formatRate } from "./constants";

/**
 * Drop zone for the compilation video, plus the transfer readout once one is
 * going. Kept as one component because the two are the same thing at
 * different moments — the file card grows into the progress panel rather
 * than being replaced by a detached bar somewhere else in the dialog.
 */

const PHASE_LABEL = {
  preparing: "Preparing upload…",
  uploading: "Uploading to S3",
  finalizing: "Finalising…",
  done: "Upload complete",
  error: "Upload failed",
  cancelled: "Upload cancelled",
};

export function UploadDropzone({ onFile, compact = false }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => accepted?.[0] && onFile(accepted[0]),
    accept: "video/*",
    multiple: false,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed",
        borderColor: isDragActive ? "#059669" : "#cbd5e1",
        borderRadius: 3,
        p: compact ? { xs: 2, sm: 3 } : { xs: 3, sm: 5 },
        textAlign: "center",
        cursor: "pointer",
        transition: "background-color .15s, border-color .15s",
        bgcolor: isDragActive ? "#ecfdf5" : "#fbfcfe",
        "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" },
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: compact ? 32 : 44, color: isDragActive ? "#059669" : "#94a3b8", mb: 0.5 }} />
      <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: 14, sm: 16 } }}>
        {isDragActive ? "Drop the video here…" : "Drag & drop a video, or click to browse"}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
        MP4 or MOV recommended · sent straight to S3, so large files upload at full speed
      </Typography>
    </Box>
  );
}

/** The picked file, with a scrubbable local preview before anything uploads. */
export function SelectedFileCard({ file, onRemove, disabled }) {
  const previewUrl = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  React.useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  if (!file) return null;

  return (
    <Fade in>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", borderColor: "#e2e8f0" }}>
        <Box
          sx={{
            bgcolor: "#0b1120",
            height: { xs: 180, sm: 240 },
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <video src={previewUrl} controls style={{ maxHeight: "100%", maxWidth: "100%" }} />
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} p={1.5} flexWrap="wrap">
          <MovieCreationIcon sx={{ color: "#64748b", flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap title={file.name}>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatBytes(file.size)}
            </Typography>
          </Box>
          {onRemove && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={onRemove}
              disabled={disabled}
              sx={{ textTransform: "none", minHeight: 40, flexShrink: 0 }}
            >
              Remove
            </Button>
          )}
        </Box>
      </Paper>
    </Fade>
  );
}

/**
 * Live transfer readout. Everything shown here is measured, not estimated
 * from a timer: percent and parts come from S3 acknowledging each part, and
 * the rate/ETA are derived from those same acknowledgements.
 */
export function UploadProgress({ state, onCancel, onRetry }) {
  const { phase, percent, uploadedParts, totalParts, bytesSent, totalBytes, bytesPerSecond, etaSeconds, resumed, error } = state;
  if (phase === "idle") return null;

  const failed = phase === "error";
  const cancelled = phase === "cancelled";
  const done = phase === "done";
  const active = ["preparing", "uploading", "finalizing"].includes(phase);
  const eta = formatEta(etaSeconds);

  const accent = failed ? "#dc2626" : cancelled ? "#64748b" : done ? "#16a34a" : "#0f766e";

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2, p: { xs: 1.25, sm: 1.75 }, borderRadius: 3,
        borderColor: failed ? "#fecaca" : "#e2e8f0",
        bgcolor: failed ? "#fef2f2" : "#f8fafc",
        minWidth: 0, overflow: "hidden",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.75}>
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
          <BoltIcon sx={{ fontSize: 15, color: accent }} />
          <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
            {PHASE_LABEL[phase] || phase}
          </Typography>
          {resumed && active && (
            <Tooltip title="S3 still had the parts from an earlier attempt, so only the missing ones are being sent.">
              <Typography
                variant="caption"
                sx={{ cursor: "help", color: "#0369a1", bgcolor: "#e0f2fe", px: 0.7, py: 0.15, borderRadius: 1, fontWeight: 600 }}
              >
                Resumed
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums", color: accent }}>
          {Math.round(percent)}%
        </Typography>
      </Box>

      <LinearProgress
        variant={phase === "preparing" ? "indeterminate" : "determinate"}
        value={percent}
        sx={{
          height: 7,
          borderRadius: 4,
          bgcolor: "#e2e8f0",
          "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: accent },
        }}
      />

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={0.85} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatBytes(bytesSent)} of {formatBytes(totalBytes)}
          {totalParts ? ` · part ${Math.min(uploadedParts + (active ? 1 : 0), totalParts)}/${totalParts}` : ""}
          {active && bytesPerSecond > 0 ? ` · ${formatRate(bytesPerSecond)}` : ""}
          {active && eta ? ` · ${eta}` : ""}
        </Typography>
        {active && onCancel && (
          <Button size="small" color="inherit" onClick={onCancel} sx={{ textTransform: "none", minWidth: 0 }}>
            Cancel
          </Button>
        )}
        {(failed || cancelled) && onRetry && (
          <Button
            size="small"
            startIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
            onClick={onRetry}
            sx={{ textTransform: "none" }}
          >
            Try again
          </Button>
        )}
      </Box>

      {failed && error && (
        <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0 }}>
          {error}
        </Alert>
      )}
      {active && (
        <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
          Parts upload in parallel directly to S3. If the connection drops, re-selecting the same
          file resumes from where it stopped.
        </Typography>
      )}
    </Paper>
  );
}
